import styled from 'styled-components';
import tw from 'twin.macro';
import { useSnapshot } from 'valtio';
import { SCREENS, SCREENS_PX } from '../responsive';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authStore, authActions } from '../../../store/authStore';
import { roleStore, roleActions, Role } from '../../../store/roleStore';
import SignUpModal from '../auth/SignUpModal';
import { useState, useEffect, useRef, type ReactNode } from 'react';
import SignInModal from '../auth/SignInModal';
import queryString from 'query-string';
import { EmailPurposeEnum } from '../../enums';
import VerifyCodeModal from '../auth/VerifyCodeModal';
import ResetPwdModal from '../auth/ResetPwdModal';
import { modalStore, modalActions } from '../../../store/authStore/modalStore';
import ForgotPwdModal from '../auth/ForgotPwdModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useUnreadChatCount } from '../../hooks/useUnreadChatCount';
import { useUnreadNotificationCount } from '../../hooks/useUnreadNotificationCount';
import { ADMIN_DEFAULT_LANDING, ADMIN_NAV_ITEMS } from '../../../constants/adminNav';
import { isServicesSite, isStaysSite } from '../../constants/siteConfig';
import { useHostCapabilities } from '../../hooks/useHostCapabilities';
import { useHostAccountGuard } from '../../hooks/useHostAccountGuard';
import { hostPageView } from '../../utils/hostCapabilities';
import { AdminOnlyBadge } from '../AdminOnlyBadge';
import {
  AlertIcon,
  CalendarIcon,
  CalendarXIcon,
  CarIcon,
  ChatIcon,
  CloseIcon,
  DocIcon,
  GridIcon,
  HashIcon,
  HelpIcon,
  HomeIcon,
  KeyIcon,
  LayersIcon,
  LogInIcon,
  LogOutIcon,
  MapPinIcon,
  MegaphoneIcon,
  MenuIcon,
  ReceiptIcon,
  SettingsIcon,
  ShieldIcon,
  StarIcon,
  StoreIcon,
  SwapIcon,
  TagIcon,
  UserIcon,
  UserPlusIcon,
  WalletIcon,
} from './navIcons';

/** Distinct icons for each admin menu row (was all GridIcon). */
const ADMIN_NAV_ICON_BY_PATH: Record<string, ReactNode> = {
  '/admin/user-status': <UserPlusIcon />,
  '/admin/brand-settings': <ShieldIcon />,
  '/admin/page-settings': <SettingsIcon />,
  '/admin/delivery-locations': <MapPinIcon />,
  '/admin/unlisted-cars': <CarIcon />,
  '/admin/hot-locations': <StarIcon />,
  '/admin/referral-commissions': <WalletIcon />,
  '/admin/damage-claims': <AlertIcon />,
  '/admin/broadcasts': <MegaphoneIcon />,
  '/admin/platform-sites': <HashIcon />,
  '/admin/verify-general-drivers-license': <KeyIcon />,
  '/admin/brands': <LayersIcon />,
  '/admin/service-providers': <ShieldIcon />,
  '/admin/service-jobs': <AlertIcon />,
  '/admin/service-workers': <UserIcon />,
  '/admin/service-tax': <ReceiptIcon />,
  '/admin/service-payouts': <WalletIcon />,
  '/admin/minimum-wages': <ReceiptIcon />,
};

const Container = styled.div`
  ${tw`relative inline-block`}
`;

const ButtonContainer = styled.div`
  ${tw`
    flex
    items-center
    justify-end
    space-x-2
  `};
`;

/** Turo-style pill: hamburger + avatar, outlined on the dark header. */
const MenuTrigger = styled.button<{ $open: boolean }>`
  ${tw`flex items-center gap-2 h-10 pl-3 pr-1 rounded-full cursor-pointer`}
  color: var(--color-on-primary);
  border: 1px solid rgb(255 255 255 / ${({ $open }) => ($open ? '0.6' : '0.28')});
  background-color: rgb(255 255 255 / ${({ $open }) => ($open ? '0.12' : '0')});
  transition:
    background-color var(--transition-normal),
    border-color var(--transition-normal);

  &:hover {
    background-color: rgb(255 255 255 / 0.12);
    border-color: rgb(255 255 255 / 0.5);
  }
  &:focus-visible {
    outline: 2px solid var(--color-on-primary);
    outline-offset: 2px;
  }

  [data-tone='light'] & {
    height: 42px;
    padding: 0 6px 0 12px;
    color: var(--color-text-primary);
    border-color: var(--color-border);
    background-color: var(--color-bg-elevated, #fff);
    box-shadow: ${({ $open }) => ($open ? '0 2px 4px rgba(0, 0, 0, 0.18)' : 'none')};
    &:hover {
      background-color: var(--color-bg-elevated, #fff);
      border-color: var(--color-border);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.18);
    }
    &:focus-visible {
      outline-color: var(--color-text-primary);
    }
  }
`;

const Avatar = styled.span`
  ${tw`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold uppercase`}
  background-color: rgb(255 255 255 / 0.16);
  color: var(--color-on-primary);
  letter-spacing: 0.02em;

  [data-tone='light'] & {
    background-color: var(--color-black);
    color: var(--color-on-primary, #fff);
  }
`;

const HeaderBellButton = styled.button`
  ${tw`relative flex items-center justify-center w-10 h-10 rounded-full cursor-pointer`}
  color: var(--color-on-primary);
  transition: background-color var(--transition-normal);

  &:hover {
    background-color: rgb(255 255 255 / 0.12);
  }

  [data-tone='light'] & {
    color: var(--color-text-primary);
    &:hover {
      background-color: var(--color-bg-secondary);
    }
  }
`;

const HeaderBellBadge = styled.span`
  ${tw`absolute top-1 right-1 text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 leading-none pointer-events-none`}
  background-color: #ef4444;
  color: #fff;
`;

/** Backdrop only for the full-screen sheet on phones. */
const MenuOverlay = styled.div<{ $isOpen: boolean }>`
  ${tw`fixed inset-0`}
  z-index: 999;
  display: none;
  background: rgb(15 23 42 / 0.4);

  @media (max-width: ${SCREENS.sm}) {
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  }
`;

/**
 * Above sm: a 300px card under the trigger, like Turo's account menu.
 * At sm and below: a full-screen sheet that slides in from the right.
 */
const SideNav = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  right: 0;
  top: calc(100% + 10px);
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  width: 300px;
  max-height: calc(100vh - 88px);
  overflow-y: auto;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-menu);
  animation: ui-pop-in var(--transition-modal);
  transform-origin: top right;

  @media (max-width: ${SCREENS.sm}) {
    position: fixed;
    inset: 0;
    width: 100vw;
    max-height: 100dvh;
    border: none;
    border-radius: 0;
    box-shadow: none;
    animation: none;
    display: flex;
    transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
    transition: transform 0.25s ease-out;
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  }
`;

/** Phone sheet header: who is signed in, and a close button. */
const NavHeader = styled.div`
  ${tw`hidden items-center justify-between gap-3 px-5 shrink-0`}
  height: 68px;
  border-bottom: 1px solid var(--color-border);

  @media (max-width: ${SCREENS.sm}) {
    display: flex;
  }
`;

const NavHeaderTitle = styled.div`
  ${tw`min-w-0`}
  font-family: var(--font-display);
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const NavHeaderSub = styled.div`
  ${tw`text-xs truncate`}
  font-family: var(--font-sans);
  font-weight: 400;
  color: var(--color-text-secondary);
`;

const CloseButton = styled.button`
  ${tw`flex items-center justify-center w-10 h-10 rounded-full cursor-pointer shrink-0`}
  color: var(--color-text-primary);

  &:hover {
    background-color: var(--color-bg-secondary);
  }
`;

const NavList = styled.ul`
  ${tw`flex flex-col list-none m-0 py-2 w-full`}
  /* No preflight in this build: clear the browser's default list indent. */
  padding-left: 0;

  @media (max-width: ${SCREENS.sm}) {
    ${tw`flex-1 overflow-y-auto pb-8`}
  }
`;

const NavItem = styled.li`
  ${tw`w-full`}
`;

const SectionLabel = styled.li`
  ${tw`px-5 pt-3 pb-1 text-[11px] font-semibold uppercase`}
  letter-spacing: 0.06em;
  color: var(--color-text-tertiary);
`;

const rowStyles = `
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 11px 20px;
  font-size: 15px;
  line-height: 22px;
  text-align: left;
  text-decoration: none;
  color: var(--color-text-primary);
  background: transparent;
  cursor: pointer;
  transition: background-color var(--transition-fast);

  > svg {
    flex-shrink: 0;
    color: var(--color-text-primary);
  }

  &:hover {
    background-color: var(--color-bg-secondary);
  }
  &:focus-visible {
    outline: none;
    background-color: var(--color-bg-secondary);
    box-shadow: inset 3px 0 0 var(--color-black);
  }

  @media (max-width: ${SCREENS.sm}) {
    padding: 14px 20px;
    font-size: 16px;
  }
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  ${rowStyles}
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
`;

const NavButton = styled.button<{ $strong?: boolean }>`
  ${rowStyles}
  font-weight: ${({ $strong }) => ($strong ? 600 : 400)};
`;

const RowLabel = styled.span`
  ${tw`flex-1 min-w-0 truncate`}
`;

const UnreadBadge = styled.span`
  ${tw`inline-flex items-center justify-center text-xs font-semibold rounded-full shrink-0`}
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  background-color: #ef4444;
  color: #fff;
`;

const ToggleButton = styled.button`
  ${tw`px-3 h-8 text-xs font-medium rounded-full cursor-pointer whitespace-nowrap md:text-sm`}
  color: var(--color-on-primary);
  background-color: rgb(255 255 255 / 0.1);
  border: 1px solid rgb(255 255 255 / 0.22);
  transition:
    background-color var(--transition-normal),
    border-color var(--transition-normal);

  &:hover {
    background-color: rgb(255 255 255 / 0.18);
    border-color: rgb(255 255 255 / 0.4);
  }
  /* Airbnb-style white header (stays guest mode). */
  [data-tone='light'] & {
    height: 40px;
    padding: 0 14px;
    color: var(--color-text-primary);
    background-color: transparent;
    border-color: transparent;
    font-weight: 600;
    &:hover {
      background-color: var(--color-bg-secondary);
      border-color: transparent;
    }
  }
`;

const Divider = styled.li`
  ${tw`my-2`}
  list-style: none;
  border-top: 1px solid var(--color-border);
`;

/** ≤ small (640px): sidebar drawer; above sm: dropdown */
const useIsSmallScreen = () => {
  const [isSmall, setIsSmall] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= SCREENS_PX.sm : false,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsSmall(window.innerWidth <= SCREENS_PX.sm);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isSmall;
};

/** Below lg (1024px): role switches stay in the burger menu, not the header bar. */
const useIsBelowLg = () => {
  const [belowLg, setBelowLg] = useState(
    typeof window !== 'undefined' ? window.innerWidth < SCREENS_PX.lg : false,
  );

  useEffect(() => {
    const handleResize = () => {
      setBelowLg(window.innerWidth < SCREENS_PX.lg);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return belowLg;
};

function NavItems({ tone = 'dark' }: { tone?: 'dark' | 'light' }) {
  const { isAuthenticated, user } = useSnapshot(authStore);
  const { role, lastNonAdminRole } = useSnapshot(roleStore);
  const { capabilities: hostCapabilities, isAdmin: isBrandAdmin } = useHostCapabilities();
  const { guard: guardHostAction } = useHostAccountGuard();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isSmallScreen = useIsSmallScreen();
  const isBelowLg = useIsBelowLg();
  const menuRef = useRef<HTMLDivElement>(null);
  const unreadChatCount = useUnreadChatCount();
  const unreadNotificationCount = useUnreadNotificationCount();

  // Close menu when clicking outside (dropdown mode). Sidebar uses overlay.
  useEffect(() => {
    if (isSmallScreen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSmallScreen]);

  // Track previous authentication state to detect logout
  const prevAuthRef = useRef(isAuthenticated);

  useEffect(() => {
    if (prevAuthRef.current && !isAuthenticated) {
      roleActions.resetRole();
    }
    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      roleActions.initRole(isAuthenticated, user);
    }
  }, [isAuthenticated, user?.isAdmin, user?.isHost, user]);

  // Check if current route matches the nav item
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const toggleRole = () => {
    if (role === Role.ADMIN) {
      return;
    }
    const newRole = role === Role.HOST ? Role.GUEST : Role.HOST;
    roleActions.setRole(newRole);
    setIsMenuOpen(false);
    navigate('/');
  };

  const enterAdminMode = () => {
    roleActions.enterAdminMode();
    navigate(ADMIN_DEFAULT_LANDING);
    setIsMenuOpen(false);
  };

  const exitAdminMode = () => {
    roleActions.exitAdminMode({ isHost: user?.isHost ?? false });
    setIsMenuOpen(false);
    navigate('/', { replace: true });
  };

  // Close menu when location changes (navigation occurs)
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Lock page scroll only behind the full-screen phone sheet (the desktop card is a dropdown).
  useEffect(() => {
    if (isMenuOpen && isSmallScreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen, isSmallScreen]);

  // Escape closes the menu, as on Turo.
  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isMenuOpen]);

  const close = () => setIsMenuOpen(false);
  const isPlatformOwner = Boolean((user as any)?.isPlatformOwner);

  const handleLogout = async () => {
    close();
    await authActions.logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  /** One menu row: icon, label, optional count. */
  const linkRow = (
    to: string,
    label: string,
    icon: ReactNode,
    count = 0,
    highlight = true,
    adminOnly = false,
  ) => (
    <NavItem key={to + label}>
      <NavLink to={to} onClick={close} $active={highlight && isActive(to)}>
        {icon}
        <RowLabel>{label}</RowLabel>
        {adminOnly && <AdminOnlyBadge testId={`admin-only-${to}`} />}
        {count > 0 && <UnreadBadge>{count > 99 ? '99+' : count}</UnreadBadge>}
      </NavLink>
    </NavItem>
  );

  /**
   * A Hosting row (D41): re-checks the host account with the server before it
   * navigates, and shows the "paused" modal instead when the host is not active.
   * Modified clicks (new tab) keep the browser's behavior.
   */
  const hostingRow = (to: string, label: string, icon: ReactNode, adminOnly = false) => (
    <NavItem key={to + label}>
      <NavLink
        to={to}
        onClick={event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          close();
          void guardHostAction(() => navigate(to));
        }}
        $active={isActive(to)}
      >
        {icon}
        <RowLabel>{label}</RowLabel>
        {adminOnly && <AdminOnlyBadge testId={`admin-only-${to}`} />}
      </NavLink>
    </NavItem>
  );

  const buttonRow = (
    key: string,
    label: string,
    icon: ReactNode,
    onClick: () => void,
    strong = false,
  ) => (
    <NavItem key={key}>
      <NavButton type="button" onClick={onClick} $strong={strong}>
        {icon}
        <RowLabel>{label}</RowLabel>
      </NavButton>
    </NavItem>
  );

  /** Trips, inbox and other everyday items for the current role. */
  const renderPrimary = (): ReactNode[] => {
    if (!isAuthenticated) return [];
    if (role === Role.ADMIN) {
      return ADMIN_NAV_ITEMS.filter(
        item =>
          (!item.platformOwnerOnly || isPlatformOwner) &&
          (!item.servicesSiteOnly || isServicesSite()),
      ).map(item =>
        linkRow(item.path, item.label, ADMIN_NAV_ICON_BY_PATH[item.path] ?? <GridIcon />),
      );
    }
    const rows: ReactNode[] = [];
    if (isServicesSite()) {
      // Services (spec 019): the Provider's tools follow in their own group.
      if (role === Role.HOST) {
        rows.push(linkRow('/services/provider', 'Today', <CalendarIcon />));
      } else {
        rows.push(linkRow('/services/jobs', 'Jobs', <CalendarIcon />));
        rows.push(linkRow('/services/sites', 'Homes and vehicles', <HomeIcon />));
        rows.push(linkRow('/guest/damage-claims', 'Resolution Center', <AlertIcon />));
      }
      rows.push(linkRow('/chat', 'Messages', <ChatIcon />, unreadChatCount));
      return rows;
    }
    if (isStaysSite()) {
      // Stays (spec 016): car-only pages are not linked; hosting tools follow in their own group.
      if (role === Role.HOST) {
        rows.push(linkRow('/stays/host', 'Today', <CalendarIcon />));
      } else {
        rows.push(linkRow('/guest/mybookings', 'Trips', <CalendarIcon />));
        rows.push(linkRow('/wishlists', 'Wishlists', <TagIcon />));
        rows.push(linkRow('/guest/damage-claims', 'Resolution Center', <AlertIcon />));
      }
      rows.push(linkRow('/chat', 'Messages', <ChatIcon />, unreadChatCount));
      return rows;
    }
    if (role === Role.HOST) {
      rows.push(linkRow('/bookedmycars', 'Trips', <CalendarIcon />));
      rows.push(linkRow('/cancelled-bookings', 'Cancelled trips', <CalendarXIcon />));
    } else {
      rows.push(linkRow('/guest/mybookings', 'My bookings', <CalendarIcon />));
      rows.push(linkRow('/cancelled-bookings', 'Cancelled bookings', <CalendarXIcon />));
    }
    rows.push(linkRow('/chat', 'Inbox', <ChatIcon />, unreadChatCount));
    rows.push(linkRow('/trips-as-additional-driver', 'Allowed to drive', <KeyIcon />));
    if (role !== Role.HOST) {
      rows.push(linkRow('/guest/damage-claims', 'Damage claims', <AlertIcon />));
    }
    return rows;
  };

  /** Host tools, only in host mode. */
  const renderHosting = (): ReactNode[] => {
    if (!isAuthenticated || role !== Role.HOST) return [];
    if (isServicesSite()) {
      return [
        <SectionLabel key="provider-label">Provider</SectionLabel>,
        hostingRow('/services/provider/workers', 'Workers', <UserIcon />),
        hostingRow('/services/provider/locations', 'Locations', <MapPinIcon />),
        hostingRow('/services/provider/payouts', 'Payouts', <WalletIcon />),
        hostingRow('/services/provider/tax-info', 'Tax info', <ReceiptIcon />),
      ];
    }
    if (isStaysSite()) {
      return [
        <SectionLabel key="hosting-label">Hosting</SectionLabel>,
        hostingRow('/stays/host/listings', 'Listings', <HomeIcon />),
        hostingRow('/stays/host/reservations', 'Reservations', <CalendarIcon />),
        hostingRow('/stays/host/reviews', 'Reviews', <StarIcon />),
        hostingRow('/stays/host/claims', 'Resolution Center', <AlertIcon />),
        hostingRow('/stays/host/listings/new', 'Create a new listing', <LayersIcon />),
      ];
    }
    const links: Array<[string, string, ReactNode]> = [
      ['/cars/host/earnings', 'Earnings', <WalletIcon />],
      ['/cars/host/add-location', 'Locations', <MapPinIcon />],
      ['/cars/host/protection-plans', 'Protection plans', <ShieldIcon />],
      ['/cars/host/product-type-details', 'Product types', <LayersIcon />],
      ['/cars/host/miscellaneous-fees', 'Trip fees', <ReceiptIcon />],
      ['/cars/host/coupons', 'Coupons', <TagIcon />],
      ['/cars/host/damage-claims', 'Damage claims', <AlertIcon />],
      ['/invoices/refund-status', 'Refund invoices', <ReceiptIcon />],
      ['/manage-short-id', 'Manage ShortID', <HashIcon />],
    ];
    if (user?.isDealer) links.push(['/dealership', 'Dealership', <StoreIcon />]);
    // Spec 014a: pages this site's brand switched off for its hosts are not offered.
    // 014b: its admin still sees them, marked "Admin only".
    return [
      <SectionLabel key="hosting-label">Hosting</SectionLabel>,
      ...links
        .map(([path, label, icon]) => ({
          path,
          label,
          icon,
          view: hostPageView(path, hostCapabilities, isBrandAdmin),
        }))
        .filter(({ view }) => view !== 'hidden')
        .map(({ path, label, icon, view }) => hostingRow(path, label, icon, view === 'adminOnly')),
    ];
  };

  /** Mode switches live in the menu below lg; at lg and above they sit in the header. */
  const renderRoleSwitches = (): ReactNode[] => {
    if (!isAuthenticated) return [];
    const rows: ReactNode[] = [];
    if (isBelowLg && role === Role.ADMIN) {
      rows.push(
        buttonRow(
          'exit-admin',
          `Switch to ${lastNonAdminRole === Role.HOST ? 'hosting' : 'traveling'}`,
          <SwapIcon />,
          exitAdminMode,
        ),
      );
    }
    if (isBelowLg && role !== Role.ADMIN && user?.isHost) {
      rows.push(
        buttonRow(
          'toggle-role',
          role === Role.HOST ? 'Switch to traveling' : 'Switch to hosting',
          <SwapIcon />,
          toggleRole,
        ),
      );
    }
    if (isBelowLg && role !== Role.ADMIN && user?.isAdmin) {
      rows.push(buttonRow('enter-admin', 'Switch to admin', <GridIcon />, enterAdminMode));
    }
    if (role !== Role.ADMIN && !user?.isHost) {
      // Non-hosts apply from the profile page (phone check + application).
      rows.push(
        linkRow(
          '/profile',
          isServicesSite() ? 'Become a Provider' : 'Become a host',
          isStaysSite() ? <HomeIcon /> : <CarIcon />,
          0,
          false,
        ),
      );
    }
    return rows;
  };

  const renderSupport = (): ReactNode[] => [
    linkRow('/help', 'Help center', <HelpIcon />),
    linkRow('/policies/terms', 'Legal', <DocIcon />),
  ];

  /** Sections in Turo's order, with a divider between non-empty groups. */
  const renderMenu = () => {
    // Home sits at the top in every mode except admin, as it did before the redesign.
    const home: ReactNode[] =
      isAuthenticated && role === Role.ADMIN ? [] : [linkRow('/', 'Home', <HomeIcon />)];
    const sections: ReactNode[][] = isAuthenticated
      ? [
          [...home, ...renderPrimary()],
          renderHosting(),
          [linkRow('/profile', 'Account', <UserIcon />)],
          renderRoleSwitches(),
          renderSupport(),
          [buttonRow('logout', 'Log out', <LogOutIcon />, handleLogout)],
        ]
      : [
          home,
          [
            buttonRow(
              'login',
              'Log in',
              <LogInIcon />,
              () => {
                modalActions.openSignInModal();
                close();
              },
              true,
            ),
            buttonRow(
              'signup',
              'Sign up',
              <UserPlusIcon />,
              () => {
                modalActions.openSignUpModal();
                close();
              },
              true,
            ),
          ],
          [
            buttonRow(
              'become-host',
              isServicesSite() ? 'Become a Provider' : 'Become a host',
              <CarIcon />,
              () => {
                modalActions.openSignUpModal();
                close();
              },
            ),
          ],
          renderSupport(),
        ];

    const out: ReactNode[] = [];
    sections
      .filter(rows => rows.length > 0)
      .forEach((rows, index) => {
        if (index > 0) out.push(<Divider key={`divider-${index}`} aria-hidden="true" />);
        out.push(...rows);
      });
    return out;
  };

  const initials = (() => {
    const first = (user?.firstName || user?.displayName || user?.email || '').trim();
    const last = (user?.lastName || '').trim();
    return `${first.charAt(0)}${last.charAt(0)}`.trim();
  })();

  const headerName = isAuthenticated
    ? user?.firstName || user?.displayName || 'Your account'
    : 'Welcome';

  return (
    <Container ref={menuRef} data-tone={tone}>
      <ButtonContainer>
        {!isBelowLg && isAuthenticated && role === Role.ADMIN && (
          <ToggleButton onClick={exitAdminMode}>
            Switch to {lastNonAdminRole === Role.HOST ? 'host' : 'guest'}
          </ToggleButton>
        )}

        {!isBelowLg && isAuthenticated && role !== Role.ADMIN && user?.isHost && (
          <ToggleButton onClick={toggleRole}>
            Switch to {role === Role.HOST ? 'guest' : 'host'}
          </ToggleButton>
        )}

        {!isBelowLg && isAuthenticated && role !== Role.ADMIN && user?.isAdmin && (
          <ToggleButton onClick={enterAdminMode}>Switch to admin</ToggleButton>
        )}

        {isAuthenticated && (
          <HeaderBellButton
            type="button"
            aria-label={
              unreadNotificationCount > 0
                ? `Notifications, ${unreadNotificationCount} unread`
                : 'Notifications'
            }
            onClick={() => navigate('/chat?tab=notifications')}
          >
            <FontAwesomeIcon icon={faBell} />
            {unreadNotificationCount > 0 && (
              <HeaderBellBadge>
                {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
              </HeaderBellBadge>
            )}
          </HeaderBellButton>
        )}

        <MenuTrigger
          type="button"
          $open={isMenuOpen}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
          aria-haspopup="menu"
        >
          <MenuIcon />
          <Avatar aria-hidden="true">
            {isAuthenticated && initials ? initials : <UserIcon />}
          </Avatar>
        </MenuTrigger>
      </ButtonContainer>

      <MenuOverlay $isOpen={isMenuOpen} onClick={close} aria-hidden />

      <SideNav
        $isOpen={isMenuOpen}
        role="dialog"
        aria-label="Menu"
        aria-modal={isSmallScreen && isMenuOpen}
      >
        <NavHeader>
          <NavHeaderTitle>
            {headerName}
            {isAuthenticated && user?.email && <NavHeaderSub>{user.email}</NavHeaderSub>}
          </NavHeaderTitle>
          <CloseButton type="button" onClick={close} aria-label="Close menu">
            <CloseIcon />
          </CloseButton>
        </NavHeader>
        <NavList>{renderMenu()}</NavList>
      </SideNav>

      <AuthModals />
    </Container>
  );
}

export default NavItems;

export const AuthModals = () => {
  const { isAuthenticated: storeAuthenticated, tokens } = useSnapshot(authStore);
  // Real GraphQL session: store flag + an actual access token. OIDC `auth.isAuthenticated`
  // can stay true after logout (we don't clear the OIDC session), which would otherwise
  // auto-close the in-place sign-in modal even though the user has no token.
  const hasValidSession = storeAuthenticated && !!tokens?.accessToken;
  const { pathname, search } = useLocation();
  const queryParams = queryString.parse(search);
  const isSignUpVerifyPath = pathname === '/signUpVerify';
  const isSignInPath = pathname === '/signIn';
  const isVerifyCode = isSignUpVerifyPath || queryParams.purpose === EmailPurposeEnum.VERIFICATION;
  const isResetPwd = queryParams.purpose === EmailPurposeEnum.RESET_PASSWORD;
  const emailFromUrl = queryParams.email
    ? decodeURIComponent(String(queryParams.email))
    : undefined;

  // Already signed in — do not reopen auth modals from /signIn or /signUpVerify URLs
  useEffect(() => {
    if (hasValidSession && (isSignInPath || isSignUpVerifyPath || queryParams.signin === 'true')) {
      modalActions.closeAllModals();
      return;
    }

    modalActions.initializeFromParams({
      signin: queryParams.signin as string,
      isSignInPath,
      isVerifyCode,
      isResetPwd,
      email: emailFromUrl,
    });
  }, [
    pathname,
    search,
    isSignInPath,
    isSignUpVerifyPath,
    isVerifyCode,
    isResetPwd,
    emailFromUrl,
    hasValidSession,
    queryParams.signin,
  ]);

  const {
    signInModalOpen,
    signUpModalOpen,
    verifyCodeModalOpen,
    resetPwdModalOpen,
    forgotPwdModalOpen,
  } = useSnapshot(modalStore);

  return (
    <>
      <SignInModal isOpen={signInModalOpen} onClose={modalActions.closeSignInModal} />
      <SignUpModal isOpen={signUpModalOpen} onClose={modalActions.closeSignUpModal} />
      <VerifyCodeModal isOpen={verifyCodeModalOpen} onClose={modalActions.closeVerifyCodeModal} />
      <ResetPwdModal isOpen={resetPwdModalOpen} />
      <ForgotPwdModal isOpen={forgotPwdModalOpen} />
    </>
  );
};
